import { useEffect, useRef, useState } from "react";

export type ReadyTransitionPhase = "connecting" | "leaving" | "ready";

const DEFAULT_DURATION_MS = 280;

/**
 * Turns a plain `ready` boolean into a 3-phase handoff so the connection splash can
 * fade out instead of being replaced by the interface on the same frame:
 *   false            → "connecting" (render the splash)
 *   true (just now)  → "leaving"    (render the splash with its fade-out class)
 *   true (settled)   → "ready"      (render the interface)
 *
 * `duration` must match the CSS fade-out animation's length (see
 * `.connection-splash--leaving` in viewer.css) — it's how long the splash stays
 * mounted to finish that animation before the caller swaps to the real interface.
 * Dropping back to `ready: false` (e.g. a reconnect) snaps back immediately; there's
 * no reverse animation, since that transition isn't the one being smoothed here.
 */
export function useReadyTransition(
  ready: boolean,
  duration: number = DEFAULT_DURATION_MS
): ReadyTransitionPhase {
  const [phase, setPhase] = useState<ReadyTransitionPhase>(ready ? "ready" : "connecting");
  const timerRef = useRef<number | undefined>(undefined);

  useEffect(() => {
    if (ready) {
      if (phase === "connecting") {
        setPhase("leaving");
        timerRef.current = window.setTimeout(() => setPhase("ready"), duration);
      }
    } else if (phase !== "connecting") {
      window.clearTimeout(timerRef.current);
      setPhase("connecting");
    }
    return () => window.clearTimeout(timerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready]);

  return phase;
}
